import {
  Copy,
  ExternalLink,
  Folder,
  Link2,
  Merge,
  Pencil,
  Trash2,
  Type,
  X,
  Brain,
  BookMarked,
  Star,
  Shield,
  Globe,
} from "lucide-react";
import { cn } from "@/lib/utils";
import {
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Switch } from "@/components/ui/switch";
import { EntityLockButton } from "@/components/EntityLockButton";

export type DocumentActionsItem = {
  id: string;
  title?: string | null;
  itemType: "note" | "reference";
  folderId?: string | null;
  includeInRag?: boolean;
  isLocked?: boolean;
  url?: string | null;
  type?: string | null;
  tags?: string[] | null;
};

function isExpertItem(type: string | null | undefined, tags: string[] | null | undefined): boolean {
  const lowerType = type?.toLowerCase() || "";
  const lowerTags = Array.isArray(tags) ? tags.map((t) => t.toLowerCase()) : [];
  return (
    lowerType.includes("sme") ||
    lowerType.includes("expert") ||
    lowerTags.includes("sme") ||
    lowerTags.includes("expert")
  );
}

export interface DocumentActionsMenuContentProps {
  item: DocumentActionsItem;
  isEmbedded?: boolean;
  onEdit: (item: DocumentActionsItem) => void;
  onRename: (item: DocumentActionsItem) => void;
  onDuplicate: (item: DocumentActionsItem) => void;
  onMerge: (item: DocumentActionsItem) => void;
  onManageLinks: (item: DocumentActionsItem) => void;
  onReembedForSearch: (item: DocumentActionsItem) => void;
  onEmbedForSearch: (item: DocumentActionsItem) => void;
  onRemoveFromSearch?: (item: DocumentActionsItem) => void;
  onToggleIncludeInRag: (item: DocumentActionsItem, value: boolean) => void;
  onMoveToFolder: (item: DocumentActionsItem) => void;
  onRemoveFromFolder: (item: DocumentActionsItem) => void;
  onDelete: (item: DocumentActionsItem) => void;
  onOpenLink?: (item: DocumentActionsItem) => void;
  onViewWorks?: (item: DocumentActionsItem) => void;
  isFavorite?: boolean;
  onToggleFavorite?: (item: DocumentActionsItem) => void;
  isSourceOfTruth?: boolean;
  onToggleSourceOfTruth?: (item: DocumentActionsItem) => void;
  onCopyPublicLink?: (item: DocumentActionsItem) => void;
  showCopyPublicLink?: boolean;
  stopPropagation?: boolean;
}

export function DocumentActionsMenuContent({
  item,
  isEmbedded = false,
  onEdit,
  onRename,
  onDuplicate,
  onMerge,
  onManageLinks,
  onReembedForSearch,
  onEmbedForSearch,
  onRemoveFromSearch,
  onToggleIncludeInRag,
  onMoveToFolder,
  onRemoveFromFolder,
  onDelete,
  onOpenLink,
  onViewWorks,
  isFavorite = false,
  onToggleFavorite,
  isSourceOfTruth = false,
  onToggleSourceOfTruth,
  onCopyPublicLink,
  showCopyPublicLink = false,
  stopPropagation = true,
}: DocumentActionsMenuContentProps) {
  const stop = (e: React.MouseEvent) => {
    if (stopPropagation) e.stopPropagation();
  };

  if (item.itemType === "note") {
    return (
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={(e) => { stop(e); onEdit(item); }}>
          <Pencil className="h-4 w-4 mr-2" />
          Edit
        </DropdownMenuItem>
        {onToggleFavorite && (
          <DropdownMenuItem onClick={(e) => { stop(e); onToggleFavorite(item); }}>
            <Star
              className={cn(
                "h-4 w-4 mr-2",
                isFavorite && "fill-amber-500 text-amber-500"
              )}
            />
            {isFavorite ? "Remove from favorites" : "Add to favorites"}
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onClick={(e) => { stop(e); onRename(item); }}>
          <Type className="h-4 w-4 mr-2" />
          Rename
        </DropdownMenuItem>
        <DropdownMenuItem onClick={(e) => { stop(e); onDuplicate(item); }}>
          <Copy className="h-4 w-4 mr-2" />
          Duplicate
        </DropdownMenuItem>
        {showCopyPublicLink && onCopyPublicLink && (
          <DropdownMenuItem
            onClick={(e) => { stop(e); onCopyPublicLink(item); }}
            data-testid={`menu-copy-public-link-${item.id}`}
          >
            <Globe className="h-4 w-4 mr-2" />
            Copy public link
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onClick={(e) => { stop(e); onMerge(item); }}>
          <Merge className="h-4 w-4 mr-2" />
          Merge into...
        </DropdownMenuItem>
        <DropdownMenuItem onClick={(e) => { stop(e); onManageLinks(item); }}>
          <Link2 className="h-4 w-4 mr-2" />
          Manage Links
        </DropdownMenuItem>
        {onToggleSourceOfTruth && item.itemType === "note" && (
          <DropdownMenuItem onClick={(e) => { stop(e); onToggleSourceOfTruth(item); }}>
            <Shield className={cn("h-4 w-4 mr-2", isSourceOfTruth && "text-primary")} />
            {isSourceOfTruth ? "Remove from Source of Truth" : "Mark as Source of Truth"}
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator />
        {isEmbedded ? (
          <>
            <DropdownMenuItem onClick={(e) => { stop(e); onReembedForSearch(item); }}>
              <Brain className="h-4 w-4 mr-2" />
              Re-embed for search
            </DropdownMenuItem>
            {onRemoveFromSearch && (
              <DropdownMenuItem onClick={(e) => { stop(e); onRemoveFromSearch(item); }}>
                <X className="h-4 w-4 mr-2" />
                Remove from search
              </DropdownMenuItem>
            )}
          </>
        ) : (
          <DropdownMenuItem onClick={(e) => { stop(e); onEmbedForSearch(item); }}>
            <Brain className="h-4 w-4 mr-2" />
            Embed for search
          </DropdownMenuItem>
        )}
        <DropdownMenuItem
          onSelect={(e) => e.preventDefault()}
          className="flex items-center justify-between gap-4 cursor-default"
          onClick={stop}
        >
          <span>Include</span>
          <Switch
            checked={item.includeInRag !== false}
            onCheckedChange={(checked) => onToggleIncludeInRag(item, checked)}
            onClick={(e) => e.stopPropagation()}
            data-testid={`switch-include-rag-${item.id}`}
          />
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <div className="px-2 py-1.5" onClick={(e) => e.stopPropagation()}>
          <EntityLockButton
            entityType="document"
            entityId={item.id}
            isLocked={item.isLocked === true}
            size="menu"
            customTitle={item.isLocked ? "Unlock document" : "Lock document"}
          />
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={(e) => { stop(e); onMoveToFolder(item); }}>
          <Folder className="h-4 w-4 mr-2" />
          Move to Folder...
        </DropdownMenuItem>
        {item.folderId && (
          <DropdownMenuItem onClick={(e) => { stop(e); onRemoveFromFolder(item); }}>
            <X className="h-4 w-4 mr-2" />
            Remove from Folder
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          className="text-destructive focus:text-destructive"
          onClick={(e) => { stop(e); onDelete(item); }}
        >
          <Trash2 className="h-4 w-4 mr-2" />
          Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    );
  }

  // Reference
  return (
    <DropdownMenuContent align="end">
      {item.url && onOpenLink && (
        <DropdownMenuItem onClick={(e) => { stop(e); onOpenLink(item); }}>
          <ExternalLink className="h-4 w-4 mr-2" />
          Open Link
        </DropdownMenuItem>
      )}
      {isExpertItem(item.type, item.tags ?? undefined) && onViewWorks && (
        <DropdownMenuItem onClick={(e) => { stop(e); onViewWorks(item); }}>
          <BookMarked className="h-4 w-4 mr-2" />
          View Works
        </DropdownMenuItem>
      )}
      {onToggleFavorite && (
        <DropdownMenuItem onClick={(e) => { stop(e); onToggleFavorite(item); }}>
          <Star
            className={cn(
              "h-4 w-4 mr-2",
              isFavorite && "fill-amber-500 text-amber-500"
            )}
          />
          {isFavorite ? "Remove from favorites" : "Add to favorites"}
        </DropdownMenuItem>
      )}
      <DropdownMenuItem onClick={(e) => { stop(e); onManageLinks(item); }}>
        <Link2 className="h-4 w-4 mr-2" />
        Manage Links
      </DropdownMenuItem>
      <DropdownMenuSeparator />
      <div className="px-2 py-1.5" onClick={(e) => e.stopPropagation()}>
        <EntityLockButton
          entityType="document"
          entityId={item.id}
          isLocked={item.isLocked === true}
          size="menu"
          customTitle={item.isLocked ? "Unlock document" : "Lock document"}
        />
      </div>
      <DropdownMenuSeparator />
      <DropdownMenuItem onClick={(e) => { stop(e); onMoveToFolder(item); }}>
        <Folder className="h-4 w-4 mr-2" />
        Move to Folder...
      </DropdownMenuItem>
      {item.folderId && (
        <DropdownMenuItem onClick={(e) => { stop(e); onRemoveFromFolder(item); }}>
          <X className="h-4 w-4 mr-2" />
          Remove from Folder
        </DropdownMenuItem>
      )}
    </DropdownMenuContent>
  );
}
